import type { ParsedEdge } from './flowchart-parser-edge-chain';
import {
  FlowchartLabelOrigin,
  type FlowchartDirection,
  type FlowchartEdge,
  type FlowchartNode,
  type FlowchartNodeShape,
  type FlowchartNodeStyle,
  type FlowchartSubgraph,
  type ParsedFlowchart,
} from './flowchart-parser-model';
import type { ParsedNodeReference } from './flowchart-parser-node-expression';

export interface FlowchartParserState {
  nodesById: Map<string, FlowchartNode>;
  subgraphs: FlowchartSubgraph[];
  edges: FlowchartEdge[];
  classDefs: Map<string, FlowchartNodeStyle>;
  unsupportedDirectives: string[];
  pendingClassNamesById: Map<string, string[]>;
  pendingStylesById: Map<string, FlowchartNodeStyle>;
  stack: string[];
}

interface NodeLabelSelectorInput {
  prior: FlowchartNode | undefined;
  ref: ParsedNodeReference;
}

interface UpsertedNodeBuildInput {
  prior: FlowchartNode | undefined;
  ref: ParsedNodeReference;
  parent: string | null;
  pendingClassNames: string[];
  pendingStyle: FlowchartNodeStyle | null;
}

interface NodeOriginRule {
  matches: (input: NodeLabelSelectorInput) => boolean;
  select: (input: NodeLabelSelectorInput) => FlowchartLabelOrigin;
}

interface UpsertNodeInput {
  nodesById: Map<string, FlowchartNode>;
  ref: ParsedNodeReference;
  parent: string | null;
  pendingClassNamesById: Map<string, string[]>;
  pendingStylesById: Map<string, FlowchartNodeStyle>;
}

interface ParsedFlowchartBuildInput {
  direction: FlowchartDirection;
  state: FlowchartParserState;
}

type NodeLabelSelector = (input: NodeLabelSelectorInput) => string | null;

export function createFlowchartParserState(): FlowchartParserState {
  return {
    nodesById: new Map<string, FlowchartNode>(),
    subgraphs: [],
    edges: [],
    classDefs: new Map<string, FlowchartNodeStyle>(),
    unsupportedDirectives: [],
    pendingClassNamesById: new Map<string, string[]>(),
    pendingStylesById: new Map<string, FlowchartNodeStyle>(),
    stack: [],
  };
}

export function currentParent(stack: readonly string[]): string | null {
  return stack.length > 0 ? stack[stack.length - 1] ?? null : null;
}

export function mergeClassNames(
  ...groups: ReadonlyArray<readonly string[] | undefined>
): string[] {
  return groups.reduce<string[]>((merged, group) => {
    if (!group) {
      return merged;
    }
    return group.reduce<string[]>((next, className) => {
      return next.includes(className) ? next : [...next, className];
    }, merged);
  }, []);
}

function selectExplicitNodeLabel(input: NodeLabelSelectorInput): string | null {
  return input.ref.label;
}

function selectInheritedNodeLabel(input: NodeLabelSelectorInput): string | null {
  return input.prior?.label ?? input.ref.label ?? null;
}

const NODE_LABEL_SELECTOR_BY_ORIGIN: Readonly<Record<FlowchartLabelOrigin, NodeLabelSelector>> = {
  [FlowchartLabelOrigin.BareDeclaration]: selectInheritedNodeLabel,
  [FlowchartLabelOrigin.ExplicitLabel]: selectExplicitNodeLabel,
  [FlowchartLabelOrigin.ImplicitEdgeRef]: selectInheritedNodeLabel,
  [FlowchartLabelOrigin.SubgraphRef]: selectInheritedNodeLabel,
  [FlowchartLabelOrigin.Unresolved]: selectInheritedNodeLabel,
};

function selectNodeLabel(input: NodeLabelSelectorInput): string | null {
  return NODE_LABEL_SELECTOR_BY_ORIGIN[input.ref.labelOrigin](input);
}

function isExplicitRefOrigin(input: NodeLabelSelectorInput): boolean {
  return input.ref.labelOrigin === FlowchartLabelOrigin.ExplicitLabel;
}

function hasNoPriorNode(input: NodeLabelSelectorInput): boolean {
  return !input.prior;
}

function hasExplicitPriorOrigin(input: NodeLabelSelectorInput): boolean {
  return input.prior?.labelOrigin === FlowchartLabelOrigin.ExplicitLabel;
}

function isBareRefOrigin(input: NodeLabelSelectorInput): boolean {
  return input.ref.labelOrigin === FlowchartLabelOrigin.BareDeclaration;
}

function selectRefOrigin(input: NodeLabelSelectorInput): FlowchartLabelOrigin {
  return input.ref.labelOrigin;
}

function selectExplicitOrigin(): FlowchartLabelOrigin {
  return FlowchartLabelOrigin.ExplicitLabel;
}

function selectPriorOrigin(input: NodeLabelSelectorInput): FlowchartLabelOrigin {
  return input.prior?.labelOrigin ?? input.ref.labelOrigin;
}

const NODE_ORIGIN_RULES: ReadonlyArray<NodeOriginRule> = [
  { matches: isExplicitRefOrigin, select: selectExplicitOrigin },
  { matches: hasNoPriorNode, select: selectRefOrigin },
  { matches: hasExplicitPriorOrigin, select: selectExplicitOrigin },
  { matches: isBareRefOrigin, select: selectRefOrigin },
];

function nextLabelOrigin(input: NodeLabelSelectorInput): FlowchartLabelOrigin {
  const matchedRule = NODE_ORIGIN_RULES.find((rule) => rule.matches(input));
  return matchedRule ? matchedRule.select(input) : selectPriorOrigin(input);
}

function selectNodeShape(input: NodeLabelSelectorInput): FlowchartNodeShape | null {
  return input.ref.shape ?? input.prior?.shape ?? null;
}

function selectNodeParent(input: {
  prior: FlowchartNode | undefined;
  parent: string | null;
}): string | null {
  return input.prior?.parent ?? input.parent;
}

function selectInlineStyle(input: {
  prior: FlowchartNode | undefined;
  pendingStyle: FlowchartNodeStyle | null;
}): FlowchartNodeStyle | null {
  return input.prior?.inlineStyle ?? input.pendingStyle;
}

function buildUpsertedNode(input: UpsertedNodeBuildInput): FlowchartNode {
  const labelInput = { prior: input.prior, ref: input.ref };
  return {
    id: input.ref.id,
    label: selectNodeLabel(labelInput),
    labelOrigin: nextLabelOrigin(labelInput),
    shape: selectNodeShape(labelInput),
    parent: selectNodeParent({ prior: input.prior, parent: input.parent }),
    classNames: mergeClassNames(
      input.prior?.classNames,
      input.ref.classNames,
      input.pendingClassNames,
    ),
    inlineStyle: selectInlineStyle({
      prior: input.prior,
      pendingStyle: input.pendingStyle,
    }),
  };
}

export function upsertNode(input: UpsertNodeInput): void {
  const prior = input.nodesById.get(input.ref.id);
  const pendingClassNames = input.pendingClassNamesById.get(input.ref.id) ?? [];
  const pendingStyle = input.pendingStylesById.get(input.ref.id) ?? null;
  input.nodesById.set(
    input.ref.id,
    buildUpsertedNode({
      prior,
      ref: input.ref,
      parent: input.parent,
      pendingClassNames,
      pendingStyle,
    }),
  );
  input.pendingClassNamesById.delete(input.ref.id);
  input.pendingStylesById.delete(input.ref.id);
}

function makeEdgeId(input: { edges: readonly FlowchartEdge[]; edgeId: string | null }): string {
  if (!input.edgeId) {
    return `edge-${input.edges.length}`;
  }
  const alreadyUsed = input.edges.some((edge) => edge.id === input.edgeId);
  return alreadyUsed ? `${input.edgeId}-${input.edges.length}` : input.edgeId;
}

export function commitParsedEdge(input: {
  parsedEdge: ParsedEdge;
  edges: FlowchartEdge[];
  pendingClassNamesById: Map<string, string[]>;
}): void {
  const id = makeEdgeId({ edges: input.edges, edgeId: input.parsedEdge.edgeId });
  const pendingEdgeClasses = input.parsedEdge.edgeId
    ? input.pendingClassNamesById.get(input.parsedEdge.edgeId) ?? []
    : [];
  input.edges.push({
    id,
    edgeId: input.parsedEdge.edgeId,
    from: input.parsedEdge.from.id,
    to: input.parsedEdge.to.id,
    label: input.parsedEdge.label,
    kind: input.parsedEdge.kind,
    startMarker: input.parsedEdge.startMarker,
    endMarker: input.parsedEdge.endMarker,
    classNames: pendingEdgeClasses,
  });
  if (input.parsedEdge.edgeId) {
    input.pendingClassNamesById.delete(input.parsedEdge.edgeId);
  }
}

function normalizeReferenceAlias(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
}

function isLikelyUnresolvedSubgraphAlias(input: {
  id: string;
  subgraphs: readonly FlowchartSubgraph[];
}): boolean {
  const normalizedId = normalizeReferenceAlias(input.id);
  if (!normalizedId) {
    return false;
  }

  return input.subgraphs.some((subgraph) => {
    const candidates = [subgraph.id, subgraph.label ?? ''].map((candidate) =>
      normalizeReferenceAlias(candidate),
    );
    return candidates.some(
      (candidate) => candidate !== normalizedId && candidate.startsWith(normalizedId),
    );
  });
}

function isImplicitSubgraphRef(input: {
  node: FlowchartNode;
  subgraphIds: ReadonlySet<string>;
}): boolean {
  return (
    input.subgraphIds.has(input.node.id) &&
    input.node.labelOrigin === FlowchartLabelOrigin.ImplicitEdgeRef
  );
}

function isImplicitUnresolvedAlias(input: {
  node: FlowchartNode;
  subgraphs: readonly FlowchartSubgraph[];
}): boolean {
  return (
    input.node.labelOrigin === FlowchartLabelOrigin.ImplicitEdgeRef &&
    isLikelyUnresolvedSubgraphAlias({ id: input.node.id, subgraphs: input.subgraphs })
  );
}

function asSubgraphRefNode(node: FlowchartNode): FlowchartNode {
  return { ...node, label: null, labelOrigin: FlowchartLabelOrigin.SubgraphRef, shape: null };
}

function asUnresolvedNode(node: FlowchartNode): FlowchartNode {
  return { ...node, label: null, labelOrigin: FlowchartLabelOrigin.Unresolved, shape: null };
}

function finalizeNodeOrigin(input: {
  node: FlowchartNode;
  subgraphIds: ReadonlySet<string>;
  subgraphs: readonly FlowchartSubgraph[];
}): FlowchartNode {
  if (isImplicitSubgraphRef(input)) {
    return asSubgraphRefNode(input.node);
  }
  if (isImplicitUnresolvedAlias(input)) {
    return asUnresolvedNode(input.node);
  }
  return input.node;
}

function finalizeNodeOrigins(input: {
  nodes: readonly FlowchartNode[];
  subgraphs: readonly FlowchartSubgraph[];
}): FlowchartNode[] {
  const subgraphIds = new Set(input.subgraphs.map((subgraph) => subgraph.id));
  return input.nodes.map((node) =>
    finalizeNodeOrigin({ node, subgraphIds, subgraphs: input.subgraphs }),
  );
}

export function buildParsedFlowchart(input: ParsedFlowchartBuildInput): ParsedFlowchart | null {
  const nodes = finalizeNodeOrigins({
    nodes: Array.from(input.state.nodesById.values()),
    subgraphs: input.state.subgraphs,
  });

  if (
    nodes.length === 0 &&
    input.state.edges.length === 0 &&
    input.state.subgraphs.length === 0
  ) {
    return null;
  }

  return {
    direction: input.direction,
    nodes,
    edges: input.state.edges,
    subgraphs: input.state.subgraphs,
    classDefs: input.state.classDefs,
    unsupportedDirectives: input.state.unsupportedDirectives,
  };
}
